import { useEffect, useState } from 'react';
import ApiDataBase from 'server';
import { TrashIcon, Plus, Warning } from 'assets/icons';
import Button from 'components/button';
import Tooltip from 'components/tooltips/Tooltips';
import {
  amountFromPercentage,
  percentageFromAmount,
  formatPercentage,
  sortRanges,
} from 'helper/coefficientRange';

const TbodyCompanyCoeff = ({ rows, setRows, errors, readOnly }) => {
  const [minimumWage, setMinimumWage] = useState(0);
  // Only the focused cell is held as raw text. The inputs are controlled, so
  // they render the parsed state, which would swallow a separator while it is
  // being typed ("1," parses to 1 and would render back as "1"). A single
  // draft describing the focused cell cannot go stale when a row is removed or
  // the table re-sorts, unlike a per-row map keyed on a shifting index.
  const [draft, setDraft] = useState(null);

  const draftText = (index, field) =>
    draft && draft.index === index && draft.field === field ? draft.text : null;

  const editDraft = (index, field, text) => setDraft({ index, field, text });
  const clearDraft = () => setDraft(null);

  useEffect(() => {
    ApiDataBase.getMinimumWage(
      (data) => {
        if (data?.hourly?.gross) setMinimumWage(data.hourly.gross);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, []);

  const errorFor = (index, field) =>
    errors.some((error) => error.index === index && error.field === field);

  // The banner only ever shows errors[0].message, so on a table failing several
  // rules at once the icon is the only thing tying a field to its own rule.
  const messageFor = (index, field) =>
    errors.find((error) => error.index === index && error.field === field)
      ?.message;

  const updateField = (index, field, value) => {
    const parsed = value === '' ? null : parseFloat(value.replace(',', '.'));
    setRows((previous) =>
      previous.map((row, i) =>
        i === index
          ? { ...row, [field]: Number.isNaN(parsed) ? null : parsed }
          : row,
      ),
    );
  };

  // The montant -> percentage conversion is lossy at two decimals, so
  // recomputing on an unchanged value would drift the stored percentage
  // (9.03 -> 13.11 -> 9.0682). Only a real edit may touch it — hence the
  // conversion on blur rather than on every keystroke.
  const updateAmount = (index, value) => {
    const raw = String(value).trim();

    // An emptied montant clears the percentage instead of silently keeping the
    // previous one: on a billing screen the cell must describe what will be
    // saved, and an empty range is exactly what R2 then refuses to confirm.
    if (raw === '') {
      setRows((previous) =>
        previous.map((row, i) =>
          i === index ? { ...row, percentage: null } : row,
        ),
      );
      return;
    }

    // Anything else unparseable is discarded and the cell falls back to the
    // stored montant on the next render, so display and state still agree.
    const typed = parseFloat(raw.replace(',', '.'));
    if (!Number.isFinite(typed)) return;

    const current = amountFromPercentage(minimumWage, rows[index]?.percentage);
    if (current !== null && typed === current) return;

    const percentage = percentageFromAmount(minimumWage, typed);
    setRows((previous) =>
      previous.map((row, i) => (i === index ? { ...row, percentage } : row)),
    );
  };

  const addRow = () =>
    setRows((previous) => [
      ...previous,
      { percentage: null, delegation: null, management: null },
    ]);

  // Removing a row shifts every source index above it, so a draft still
  // pointing at one of them would land on the wrong cell.
  const removeRow = (index) => {
    clearDraft();
    setRows((previous) => previous.filter((_, i) => i !== index));
  };

  // sortRanges reorders rows for display, but `rows` state (and the array
  // sent to postCoefficient) stays in its original order. Tagging each row
  // with its source index before sorting lets every handler act on the
  // right row in `rows` regardless of where it lands on screen — mixing the
  // display position with the source index would edit or delete the wrong
  // row whenever the two orders diverge.
  const sortedRows = sortRanges(
    rows.map((row, sourceIndex) => ({ ...row, sourceIndex })),
  );

  const inputClass = (index, field) =>
    `border rounded px-2 py-1 w-24 ${
      errorFor(index, field) ? 'border-red-500 text-red-500' : ''
    }`;

  // The red border alone is easy to miss on a dense table; the mockup marks
  // each faulty value with the icon as well.
  const errorIcon = (index, field) =>
    errorFor(index, field) ? (
      <Tooltip text={messageFor(index, field)} placement={'top'}>
        <span className={'my-auto shrink-0'}>
          <Warning wh={20} color={'#EF4444'} />
        </span>
      </Tooltip>
    ) : null;

  return (
    <>
      {sortedRows.map((row, position) => (
        <tr
          key={row.sourceIndex}
          // No rule under the last range: it separates rows, and there is
          // nothing after it to separate from.
          className={`border-gray-100 ${
            position === sortedRows.length - 1 ? '' : 'border-b'
          }`}
        >
          <td className={'py-5'}>
            <div className={'flex items-center gap-1'}>
              {errorIcon(row.sourceIndex, 'percentage')}
              {position === 0 ? (
                <span className={'text-gray-500'}>{minimumWage} – </span>
              ) : (
                // Rows past the first carry no lower bound of their own; the
                // chevron gives them the lead-in the first row gets from SMIC.
                <span className={'text-gray-500'}>{'>'}</span>
              )}
              {readOnly ? (
                <span
                  className={
                    errorFor(row.sourceIndex, 'percentage')
                      ? 'text-red-500'
                      : ''
                  }
                >
                  {amountFromPercentage(minimumWage, row.percentage)}
                </span>
              ) : (
                <input
                  type={'text'}
                  className={inputClass(row.sourceIndex, 'percentage')}
                  value={
                    draftText(row.sourceIndex, 'percentage') ??
                    amountFromPercentage(minimumWage, row.percentage) ??
                    ''
                  }
                  onChange={(e) =>
                    editDraft(row.sourceIndex, 'percentage', e.target.value)
                  }
                  onBlur={(e) => {
                    updateAmount(row.sourceIndex, e.target.value);
                    clearDraft();
                  }}
                />
              )}
              <span className={'text-gray-500 ml-1'}>
                ({formatPercentage(row.percentage)})
              </span>
            </div>
          </td>
          <td className={'py-5'}>
            <div className={'flex items-center gap-1'}>
              {errorIcon(row.sourceIndex, 'delegation')}
              <input
                type={'text'}
                className={inputClass(row.sourceIndex, 'delegation')}
                disabled={readOnly}
                value={
                  draftText(row.sourceIndex, 'delegation') ??
                  row.delegation ??
                  ''
                }
                onChange={(e) => {
                  editDraft(row.sourceIndex, 'delegation', e.target.value);
                  updateField(row.sourceIndex, 'delegation', e.target.value);
                }}
                onBlur={clearDraft}
              />
            </div>
          </td>
          <td className={'py-5'}>
            <div className={'flex items-center gap-1'}>
              {errorIcon(row.sourceIndex, 'management')}
              <input
                type={'text'}
                className={inputClass(row.sourceIndex, 'management')}
                disabled={readOnly}
                value={
                  draftText(row.sourceIndex, 'management') ??
                  row.management ??
                  ''
                }
                onChange={(e) => {
                  editDraft(row.sourceIndex, 'management', e.target.value);
                  updateField(row.sourceIndex, 'management', e.target.value);
                }}
                onBlur={clearDraft}
              />
            </div>
          </td>
          <td className={'py-5'}>
            {/* The first range starts at SMIC: it cannot be deleted. */}
            {position > 0 && !readOnly && (
              <Button
                size={'SMI'}
                color={'ICON'}
                onClick={() => removeRow(row.sourceIndex)}
              >
                <TrashIcon wh={20} color={'#6B7280'} />
              </Button>
            )}
          </td>
        </tr>
      ))}
      {!readOnly && (
        <tr>
          <td colSpan={4} className={'py-3'}>
            <Button size={'SM'} color={'SECONDARY'} onClick={addRow}>
              <Plus wh={20} color={'#111827'} />
              Ajouter une tranche
            </Button>
          </td>
        </tr>
      )}
    </>
  );
};

export default TbodyCompanyCoeff;
